import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import toast from 'react-hot-toast';
import HemodialysisReportModal from '../../component/admin/ModalComponents/HemodialysisReportModal';
import reportingService from '../../services/reportingService';

const HemodialysisReports = () => {
    const [showModal, setShowModal] = useState(false);
    const [patientList, setPatientList] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadPatients(),
                loadTemplates()
            ]);
        } catch (error) {
            console.error('Error loading initial data:', error);
            toast.error('Failed to load initial data');
        } finally {
            setLoading(false);
        }
    };

    const loadPatients = async () => {
        try {
            const response = await reportingService.getPatients({ limit: 100 });
            if (response.success && response.data && response.data.patients) {
                setPatientList(response.data.patients);
            }
        } catch (error) {
            console.error('Error loading patients:', error);
            toast.error('Failed to load patients');
        }
    };

    const loadTemplates = async () => {
        try {
            const response = await reportingService.getHemodialysisTemplates();
            if (response.success && response.data) {
                setTemplates(response.data);
            }
        } catch (error) {
            console.error('Error loading hemodialysis templates:', error);
            // Don't show error toast for templates as it's not critical
        }
    };

    const handleShowModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    if (loading) {
        return (
            <Container fluid className="p-4">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading hemodialysis reporting system...</p>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="mb-1">
                                <i className="fas fa-stethoscope text-primary me-2"></i>
                                Hemodialysis Reports
                            </h2>
                            <p className="text-muted mb-0">
                                Generate comprehensive hemodialysis session reports for clinical documentation
                            </p>
                        </div>
                        <Button 
                            variant="primary" 
                            onClick={handleShowModal}
                            className="d-flex align-items-center"
                        >
                            <i className="fas fa-plus me-2"></i>
                            Generate Report
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* System Status */}
            <Row className="mb-4">
                <Col lg={12}>
                    <Card className="border-info">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={3}>
                                    <div className="text-center">
                                        <i className="fas fa-heartbeat text-info fa-2x mb-2"></i>
                                        <h6 className="mb-0">System Status</h6>
                                        <small className="text-success">
                                            <i className="fas fa-check-circle me-1"></i>
                                            Online
                                        </small>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-center">
                                        <i className="fas fa-users text-primary fa-2x mb-2"></i>
                                        <h6 className="mb-0">Available Patients</h6>
                                        <strong className="text-primary">{patientList.length}</strong>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-center">
                                        <i className="fas fa-file-medical text-success fa-2x mb-2"></i>
                                        <h6 className="mb-0">Report Templates</h6>
                                        <strong className="text-success">{templates.length}</strong>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-center">
                                        <i className="fas fa-shield-alt text-warning fa-2x mb-2"></i>
                                        <h6 className="mb-0">Compliance</h6>
                                        <small className="text-success">
                                            <i className="fas fa-check-circle me-1"></i>
                                            HIPAA Ready
                                        </small>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Report Types */}
            <Row>
                <Col lg={6} className="mb-4">
                    <Card className="h-100 border-primary">
                        <Card.Header className="bg-primary text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-user me-2"></i>
                                Single Patient Session Report
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <p className="text-muted">
                                Generate a detailed clinical report for a single patient's hemodialysis session, 
                                matching hospital documentation standards.
                            </p>
                            <ul className="list-unstyled">
                                <li><i className="fas fa-check text-success me-2"></i>Pre-HD Assessment</li>
                                <li><i className="fas fa-check text-success me-2"></i>During-HD Monitoring</li>
                                <li><i className="fas fa-check text-success me-2"></i>Post-HD Evaluation</li>
                                <li><i className="fas fa-check text-success me-2"></i>Vital Signs Tracking</li>
                                <li><i className="fas fa-check text-success me-2"></i>Clinical Notes</li>
                            </ul>
                            <Button 
                                variant="outline-primary" 
                                className="w-100 mt-3"
                                onClick={handleShowModal}
                            >
                                Generate Single Patient Report
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={6} className="mb-4">
                    <Card className="h-100 border-success">
                        <Card.Header className="bg-success text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-users me-2"></i>
                                Multi-Patient Summary Report
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <p className="text-muted">
                                Generate a comprehensive summary table for multiple patients with statistical 
                                analysis and trends over specified time periods.
                            </p>
                            <ul className="list-unstyled">
                                <li><i className="fas fa-check text-success me-2"></i>Statistical Summary</li>
                                <li><i className="fas fa-check text-success me-2"></i>Trend Analysis</li>
                                <li><i className="fas fa-check text-success me-2"></i>Comparative Data</li>
                                <li><i className="fas fa-check text-success me-2"></i>Date Range Filtering</li>
                                <li><i className="fas fa-check text-success me-2"></i>Export Ready</li>
                            </ul>
                            <Button 
                                variant="outline-success" 
                                className="w-100 mt-3"
                                onClick={handleShowModal}
                            >
                                Generate Multi-Patient Report
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Information Section */}
            <Row>
                <Col lg={12}>
                    <Card className="border-warning">
                        <Card.Body>
                            <Row>
                                <Col md={8}>
                                    <h6 className="text-warning mb-2">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Important Information
                                    </h6>
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-1">
                                            <strong>Form Requirements:</strong> Reports are generated from completed hemodialysis forms (Pre-HD, During-HD, Post-HD assessments)
                                        </li>
                                        <li className="mb-1">
                                            <strong>Data Source:</strong> Form IDs FORM-41563642, FORM-8591F1B3, FORM-BEC0F708
                                        </li>
                                        <li className="mb-1">
                                            <strong>Export Format:</strong> PDF reports with timestamp and patient information
                                        </li>
                                        <li className="mb-0">
                                            <strong>Compliance:</strong> All reports maintain HIPAA compliance with audit trails
                                        </li>
                                    </ul>
                                </Col>
                                <Col md={4} className="text-center">
                                    <i className="fas fa-file-pdf text-danger fa-3x mb-2"></i>
                                    <h6 className="text-muted">PDF Generation</h6>
                                    <small className="text-muted">Professional medical reports</small>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Hemodialysis Report Modal */}
            {showModal && (
                <HemodialysisReportModal 
                    show={showModal} 
                    handleClose={handleCloseModal} 
                    patientList={patientList}
                />
            )}
        </Container>
    );
};

export default HemodialysisReports;